import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";

import {
  getContractTimeline,
  logDelayReason,
  logReminder,
} from "../services/onboarding/contracts";
import {
  ContractTimelineEvent,
  LogDelayReasonPayload,
  LogReminderPayload,
} from "../types/onboarding";

interface UseContractTimelineParams {
  id: string;
  toastRef: React.RefObject<Toast>;
  enabled?: boolean;
}

interface UseContractTimeline {
  events: ContractTimelineEvent[];
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  // Log actions return a boolean so dialogs close on success / stay open on error.
  logReminderAction: (payload: LogReminderPayload) => Promise<boolean>;
  logDelayAction: (payload: LogDelayReasonPayload) => Promise<boolean>;
}

/**
 * Owns a prospect's contract activity timeline (US4) plus the reminder/delay
 * logging actions, each of which refreshes the timeline on success.
 */
const useContractTimeline = ({
  id,
  toastRef,
  enabled = true,
}: UseContractTimelineParams): UseContractTimeline => {
  const [events, setEvents] = useState<ContractTimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Guards against out-of-order / cross-prospect responses: only the latest
  // request may write state (e.g. when the prospect id changes).
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const res = await getContractTimeline(id);
      if (requestId !== requestIdRef.current) return;
      setEvents(res?.events ?? []);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to load contract timeline", e);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [id, enabled]);

  // Clear stale events immediately when the prospect changes so we never render
  // one prospect's timeline against another while the refetch is in flight.
  useEffect(() => {
    setEvents([]);
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const logReminderAction = useCallback(
    async (payload: LogReminderPayload): Promise<boolean> => {
      try {
        await logReminder(id, payload);
        await reload();
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Reminder logged.",
          life: 3000,
        });
        return true;
      } catch (e) {
        throw e; // dialog surfaces the error inline
      }
    },
    [id, reload, toastRef]
  );

  const logDelayAction = useCallback(
    async (payload: LogDelayReasonPayload): Promise<boolean> => {
      try {
        await logDelayReason(id, payload);
        await reload();
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Delay reason logged.",
          life: 3000,
        });
        return true;
      } catch (e) {
        throw e;
      }
    },
    [id, reload, toastRef]
  );

  return { events, loading, error, reload, logReminderAction, logDelayAction };
};

export default useContractTimeline;
